import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { useAuth } from '#features/auth/auth-context';
import { useLogout } from '#features/auth/api';
import { ReauthModal } from '#features/auth/components/reauth-modal';
import { CommandPalette } from '#features/palette/components/command-palette';
import { EntryScreen } from '#features/shell/entry-screen';
import { entryOf } from '#features/shell/entry-state';
import { AppHeader } from '#features/shell/app-header';
import { useMe } from '#features/users/api';
import { embeddedViewer, useVittoSession } from '#lib/auth/vitto-session';
import { useHostMode } from '#lib/host/host-mode-context';
import { useLiveUpdates } from '#lib/sse/use-live-updates';
import { authSession } from '#lib/auth/auth-session';
import { useIdentityGuard } from '#lib/auth/use-identity-guard';

const AuthedLayout = () => {
  const { state, wasAuthenticated, retry } = useAuth();
  const me = useMe();
  const logout = useLogout();
  // an app framed by Vitto has no password to ask for: its entrance (`EmbeddedGate`) renews the session and, if the host refuses, says so
  const embedded = useHostMode().kind === 'embedded';
  const session = useVittoSession();
  const viewer = embeddedViewer(embedded, session);
  useIdentityGuard(me.data?.id, session, authSession);
  // Una sola conexión por pestaña, viva solo mientras hay sesión.
  useLiveUpdates(state === 'authenticated');

  // Cada estado de entrada dice qué pasa y qué se puede hacer (TASKS-APP-FULL-UI-1 U1b): antes `unknown` era una página en
  // blanco y un primer arranque sin servidor pintaba toda la app detrás de un modal. Quien pierde la sesión estando dentro
  // sigue en la app: el modal de reautenticación no le quita lo que estuviera escribiendo (diseño §4).
  const entry = entryOf(state, wasAuthenticated);
  if (entry === 'login') return <Navigate to="/login" replace />;
  if (entry === 'connecting' || entry === 'offline')
    return <EntryScreen kind={entry} onRetry={() => void retry()} />;

  return (
    <div className="min-h-dvh bg-background">
      {/* Lo primero que encuentra quien tabula, para no recorrer la navegación entera. */}
      <a href="#contenido" className="skip-link">
        Skip to content
      </a>
      <AppHeader
        name={me.data?.name}
        onSignOut={() => logout.mutate()}
        {...(viewer ? { embedded: viewer } : {})}
      />
      <main id="contenido">
        <Outlet />
      </main>
      <CommandPalette />
      {embedded ? null : <ReauthModal />}
    </div>
  );
};

export const Route = createFileRoute('/_authed')({ component: AuthedLayout });
